import { get } from 'lodash';
import { ISchema } from 'yup';
import { yup } from '../initYup';

type ResolveOptions = Parameters<ISchema<any, any>['resolve']>[0];

export type ParentWhenFn<TIn extends ISchema<any, any>> = (
  values: any[],
  schema: TIn,
  context?: any,
) => ISchema<any>;

export const ParentDepsKey = 'parentDeps';

declare module 'yup' {
  interface Schema {
    /**
     * Aggiunge una condizione che permette di risolvere lo schema in base a
     * uno o più valori non solo fra i nodi fratelli, ma anche fra nodi padre
     */
    parentWhen<TKeys extends string[]>(
      keys: TKeys,
      fn: ParentWhenFn<this>,
    ): this;
  }
}

// const data = {
//   select_ws: '7',
//   'select_ws@object': {
//     userId: 7,
//     name: 'Mario Rossi'
//   },
//   ripetibile: [{ a: 10, b: 10 }],  // se a è 10     ----> `^.select_ws è 10` dove `i` è l'indice dell'array di b
//   checkboxmultipla: ['opzA', 'opzB'],
//   'checkboxmultipla@options': {
//     opzA: true,
//     opzB: true,
//     opzC: false

//     // se opzB e select_ws è 7 ( path: [ { dir: Child, code: "checkboxmultipla@options" } ], code: "opzB" ) è vero -> deps è ["opzB"] -> OK
//     // ^.select_ws
//     // opzB
//   },
//   campo_cond: 10
//   // se opzA è vero ----> `checkboxmultipla@options.opzA è vero`
// };

/**
 * Funzione generica che registra il metodo un tipo di schema di yup
 */
function registerForSchema<T extends yup.Schema>(schema: (...arg: any[]) => T) {
  yup.addMethod<T>(
    schema,
    'parentWhen',
    function (this: T, keys: string[], fn: any) {
      const next = this.clone();
      const deps = keys;

      deps.forEach(dep => {
        if (
          !dep.startsWith('^') &&
          !dep.startsWith('$') &&
          !dep.startsWith('.')
        ) {
          // Yup gestisce già in autonomia le dipendenze tipo `abc.def` (figli dei sibling)
          // L'unico caso che non gestiamo probabilmente come Yup è `.figlio` cioè il figlio _del nodo corrente_, ma che non generiamo mai
          (next.deps as any).push(dep);
        }

        // Le dipendenze relative al padre vengono aggiunte come meta, per
        // poi essere gestite in `useField`.
        if (dep.startsWith('^')) {
          if (!next.spec.meta) next.spec.meta = {};
          if (!next.spec.meta?.[ParentDepsKey])
            next.spec.meta[ParentDepsKey] = [];

          next.spec.meta[ParentDepsKey].push(dep);
        }
      });

      (next as any).conditions.push(new ParentCondition(keys, fn));
      return next;
    },
  );
}

registerForSchema(yup.object);
registerForSchema(yup.array);
registerForSchema(yup.string);
registerForSchema(yup.number);
registerForSchema(yup.boolean);
registerForSchema(yup.date);
registerForSchema(yup.mixed);

/**
 * Una condizione che permette di risolvere lo schema in base a uno o più valori
 * non solo fra i nodi fratelli, ma anche fra nodi padre
 */
export class ParentCondition<
  TIn extends ISchema<any, any> = ISchema<any, any>,
> {
  static for<TIn extends ISchema<any, any>>(
    keys: string[],
    fn: ParentWhenFn<TIn>,
  ) {
    return new ParentCondition(keys, fn);
  }

  constructor(private keys: string[], private fn: ParentWhenFn<TIn>) {}

  resolve(prevSchema: TIn, options: ResolveOptions) {
    // console.log(`[ParentCondition] Resolve`, options, 'from is', options.from);
    const values = this.keys.map(key => resolveConditionKeyValue(key, options));

    const resultSchema = this.fn(values, prevSchema, options.context);

    if (resultSchema === undefined || resultSchema === prevSchema) {
      return prevSchema;
    }

    return resultSchema.resolve(options);
  }
}

/**
 * Risolve il percorso specificato, dove con `^` si intende il nodo padre.
 * I path sono divisi in segmenti dal carattere `.`.
 */
export function resolveConditionKeyValue(key: string, options: ResolveOptions) {
  // Gestione del contesto, estratto con `$`
  if (key.startsWith('$')) {
    return get(options.context, key.slice(1));
  }

  const segments = key.split('.');
  let currentScopeIndex = 0;
  for (const segment of segments) {
    if (segment === '^') {
      currentScopeIndex++;
    }
  }

  /**
   * _Sembra_ che yup faccia una run di costruzione chiamando il resolve senza passare
   * tutte le options tra cui il from. In questo lo schema viene solo "costruito" e non
   * validato, quindi possiamo ritornare `undefined`come valore senza preoccuparci dei
   * side effect.
   */
  if (!options.from || options.from.length === 0) {
    return undefined;
  }

  if (!options.from[currentScopeIndex]) {
    // console.warn(`[ParentCondition.resolve] Path ${key} is out of scope, current scope is ${currentScopeIndex} and from is`, options.from); // prettier-ignore
    return undefined;
  }

  const value = get(
    options.from[currentScopeIndex].value,
    segments.slice(currentScopeIndex).join('.'),
  );

  return value;
}
